import { classify } from 'app/pages/Teacher/Attendance/Attendance'

import { PRESCHOOL_HOLIDAYS } from './holidays'

// Centre Audit > Scorecard (2026-10-17). Every rule that turns the API's
// facts into a score out of 5 lives in this file. The API only sends Keka's
// day rows, moments per day and her Classroom Observations.
//
//   Punctuality  = on-time days / working days x 5. Working = clocked in
//                  (on time or late) + Absent or on leave on a day she had a
//                  class in Alfred (t.class_days) — leave is a miss too
//                  (Satya, 22 Oct). Keka also marks part-time teachers
//                  Absent on days with no class; those and week offs are
//                  left out (17 Oct). Preschool teachers' holidays come from
//                  the preschool calendar (holidays.js), not Keka (22 Oct).
//                  Not linked to an Alfred teacher (class_days null): there
//                  are no classes to check, so every absent and leave day
//                  counts (Satya, 22 Oct — a Centre Director's absences were
//                  all being dropped).
//                  "Late" is the Attendance page's own rule.
//   Moments      = preschool: Daily Updates on time / due (see dailyUpdates).
//                  Others: days worked with at least one moment / days
//                  worked x 5. Blank when the Keka name matches no Alfred
//                  teacher.
//   Environment,
//   Facilitation = Centre Ops > Classroom Observations (Satya, 19 Oct): Yes
//                  answers / questions answered x 5, pooled across every
//                  observation dated in the period. A question left blank
//                  was not judged and is left out.
//   Weekly Submissions,
//   Lesson Planner = preschool hand-ins done / due x 5 over the weeks whose
//                  Monday falls in the period (22 Oct, see weeklyWork).
//   Overall      = average of the sections that have a score.

export const SECTIONS = [
  { key: 'pun', label: 'Punctuality', color: '#2F7D5B' },
  { key: 'mom', label: 'Moments', color: '#3F6FB0' },
  { key: 'env', label: 'Environment setup', color: '#C07A1E' },
  { key: 'fac', label: 'Facilitation', color: '#8A4F9E' },
  { key: 'sub', label: 'Weekly Submissions', color: '#B5475A' },
  { key: 'plan', label: 'Lesson Planner', color: '#2E8C95' }
]

export const band = (v) => {
  if (v === null || v === undefined) return 'none'
  if (v >= 4) return 'good'
  if (v >= 3) return 'ok'
  return 'weak'
}

const r1 = (v) => Math.round(v * 10) / 10

const toIso = (d) => {
  const pad = (x) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`)
  d.setDate(d.getDate() + n)
  return toIso(d)
}

// Month-by-month view (Shruti, 19 Oct): the days of a month ('YYYY-MM'),
// stopping at upTo, and the month before.
export const monthDays = (ym, upTo) => {
  const out = []
  for (let d = `${ym}-01`; d.slice(0, 7) === ym; d = addDays(d, 1)) {
    if (upTo && d > upTo) break
    out.push(d)
  }
  return out
}

export const prevMonth = (ym) => addDays(`${ym}-01`, -1).slice(0, 7)

export const weekDays = (monday) =>
  Array.from({ length: 7 }, (_, i) => addDays(monday, i))

// One Keka day as the scorecard sees it.
export const dayKind = (v) => {
  const x = classify(v)
  if (x.k === 'late') return 'late'
  if (x.k === 'P' || x.k === 'MS') return 'on'
  if (x.k === 'A') return 'absent'
  if (x.k === 'L') {
    // Half-day leave with a clock-in still counts as a day at work.
    if (x.time) return (v.late || 0) > 15 ? 'late' : 'on'
    return 'leave'
  }
  if (x.k === 'WO') return 'off'
  return 'none'
}

// Centre leaders (Shruti, 22 Oct): Centre Director, Deputy Centre Director,
// (Senior) Admissions Manager — the server's LEADER_TITLE_REGEX.
export const isLeader = (t) =>
  /centre director|admissions manager/i.test(t.job || '')

// Preschool = Keka's Preschool department, or on a preschool batch in Alfred
// (the API sends t.updates for anyone on one): Early Years teachers filed
// under Centre are preschool teachers too (22 Oct).
export const isPreschool = (t) =>
  t.dept === 'Preschool' || !!(t.updates && t.updates.batches)

export const isHoliday = (t, d) => isPreschool(t) && PRESCHOOL_HOLIDAYS.has(d)

// Her day as the scorecard counts it: Keka's, with the preschool calendar on
// top — its holidays are off, and a Keka holiday it doesn't have is a
// working day she wasn't in on.
export const dayFor = (t, d) => {
  if (isHoliday(t, d)) return 'holiday'
  const v = t.days[d]
  if (isPreschool(t) && v && v.s === 'H') return 'absent'
  return dayKind(v)
}

// Absent or on leave counts as a missed day only on a day Alfred shows a
// class for her — or on any day when she is linked to no Alfred teacher.
export const counts = (t, d) =>
  t.class_days === null || (t.class_days || []).includes(d)

export const missedClass = (t, d) => {
  const k = dayFor(t, d)
  return (k === 'absent' || k === 'leave') && counts(t, d)
}

export const punctuality = (t, dates) => {
  let on = 0
  let worked = 0
  let absent = 0
  let leave = 0
  dates.forEach((d) => {
    const k = dayFor(t, d)
    if (k === 'on') on += 1
    if (k === 'on' || k === 'late') worked += 1
    if (missedClass(t, d)) {
      worked += 1
      if (k === 'leave') leave += 1
      else absent += 1
    }
  })
  return {
    on,
    worked,
    absent,
    leave,
    score: worked ? r1((on / worked) * 5) : null
  }
}

// Preschool teachers are scored on the Daily Updates check instead (the API
// sends t.updates for them): on-time updates ÷ updates due for the children
// in her batches, on the days she clocked in. Late or missing = a miss.
export const dailyUpdates = (t, dates) => {
  const empty = { req: 0, ok: 0, score: null, short: [], days: [] }
  if (!t.linked || !t.updates) return empty
  let req = 0
  let ok = 0
  const short = []
  // Every counted day, for the pop-up's bars: {d, req, ok}.
  const days = []
  dates.forEach((d) => {
    const k = dayFor(t, d)
    if (k !== 'on' && k !== 'late') return
    const day = t.updates.days[d]
    if (!day) return
    req += day.req
    ok += day.ok
    days.push({ d, req: day.req, ok: day.ok })
    if (day.ok < day.req)
      short.push({ d, miss: day.req - day.ok, req: day.req })
  })
  return { req, ok, short, days, score: req ? r1((ok / req) * 5) : null }
}

export const moments = (t, dates) => {
  if (isPreschool(t)) {
    const u = dailyUpdates(t, dates)
    return { ...u, withM: u.ok, worked: u.req, missing: [], preschool: true }
  }
  if (!t.linked) return { withM: 0, worked: 0, score: null, missing: [] }
  let withM = 0
  let worked = 0
  const missing = []
  dates.forEach((d) => {
    const k = dayFor(t, d)
    if (k !== 'on' && k !== 'late') return
    worked += 1
    if ((t.moments[d] || 0) > 0) withM += 1
    else missing.push(d)
  })
  return {
    withM,
    worked,
    missing,
    score: worked ? r1((withM / worked) * 5) : null
  }
}

// Pools every answer across the observations dated first..last (ISO), so a
// month is the share of all its answers rather than an average of averages.
export const observedBetween = (t, first, last, section) => {
  const list = (t.observations || []).filter(
    (o) => o.d >= first && o.d <= last && o[section].length
  )
  let yes = 0
  let n = 0
  list.forEach((o) =>
    o[section].forEach((q) => {
      n += 1
      if (q.a === 'yes') yes += 1
    })
  )
  return { yes, n, list, score: n ? r1((yes / n) * 5) : null }
}

export const observed = (t, mondays, section) =>
  observedBetween(
    t,
    mondays[0],
    addDays(mondays[mondays.length - 1], 6),
    section
  )

// Weekly Submissions ('evals') or Lesson Planner ('plans'): the API sends
// [due, done] per Monday, class-level — done by any of a class's teachers
// counts for all of them. Only weeks with a published weekly theme are due.
export const weeklyWork = (t, dates, kind) => {
  const w = t.weekly_work && t.weekly_work[kind]
  const out = { due: 0, done: 0, weeks: [], score: null }
  if (!w) return out
  dates.forEach((d) => {
    const x = w[d]
    if (!x) return
    out.due += x[0]
    out.done += x[1]
    out.weeks.push({ d, due: x[0], done: x[1] })
  })
  if (out.due) out.score = r1((out.done / out.due) * 5)
  return out
}

// Every section over a run of consecutive days (whole weeks, or a calendar
// month).
export const scoresForDates = (t, dates) => {
  if (!dates.length)
    return {
      pun: null,
      mom: null,
      env: null,
      fac: null,
      sub: null,
      plan: null,
      overall: null
    }
  const first = dates[0]
  const last = dates[dates.length - 1]
  const s = {
    pun: punctuality(t, dates).score,
    mom: moments(t, dates).score,
    env: observedBetween(t, first, last, 'env').score,
    fac: observedBetween(t, first, last, 'fac').score,
    sub: weeklyWork(t, dates, 'evals').score,
    plan: weeklyWork(t, dates, 'plans').score
  }
  const have = Object.values(s).filter((v) => v !== null)
  s.overall = have.length
    ? r1(have.reduce((a, b) => a + b, 0) / have.length)
    : null
  return s
}

export const scoresFor = (t, mondays) =>
  scoresForDates(t, mondays.flatMap(weekDays))

// Trend buckets: one per week, or the weeks grouped by the month their
// Monday falls in.
export const buckets = (weeks, period) => {
  if (period === 'week')
    return weeks.map((m) => ({
      label: new Date(`${m}T00:00:00`).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short'
      }),
      mondays: [m]
    }))
  const byMonth = []
  weeks.forEach((m) => {
    const label = new Date(`${m}T00:00:00`).toLocaleDateString('en-IN', {
      month: 'short',
      year: '2-digit'
    })
    const last = byMonth[byMonth.length - 1]
    if (last && last.label === label) last.mondays.push(m)
    else byMonth.push({ label, mondays: [m] })
  })
  return byMonth
}

// Average of each section across several teachers (blanks left out).
export const averageScores = (list) => {
  const out = {}
  ;[...SECTIONS.map((s) => s.key), 'overall'].forEach((k) => {
    const vals = list.map((s) => s[k]).filter((v) => v !== null)
    out[k] = vals.length
      ? r1(vals.reduce((a, b) => a + b, 0) / vals.length)
      : null
  })
  return out
}
